<template>
    <nav class="h-full overflow-y-scroll bg-white pt-32">
        <ul v-for="(section, index) in menuItems.filter(menuItem => $can(menuItem.requiredPermissions))"
            :key="index"
            class=" w-full mb-0 overflow-auto"
        >
            <li class="mb-16 px-16 xl:px-32 text-center xl:text-left first-letter:uppercase font-semibold text-body">
                {{ menuTranslations(section.title) }}
            </li>
            <li class="mb-32">
                <ul class=" w-full mb-32">
                    <li v-for="(item, subIndex) in section.items" :key="subIndex">
                        <div v-if="item.requiredPermissions ? $can(item.requiredPermissions) : true">
                            <router-link
                                :to="{ name: item.route }"
                                class="block text-center xl:flex xl:items-center text-primary py-8 xl:pb-0 xl:py-4 relative px-16 xl:px-32 group"
                            >
                                <div class="absolute left-0 top-0 w-4 h-full rounded-r hidden bg-secondary"></div>
                                <span v-if="item.icon"
                                      class="h-40 w-40 mx-auto xl:ml-0 xl:mr-16 rounded-32 bg-primary-5 text-primary-80 flex items-center justify-center flex-none mb-4 group-hover:bg-secondary group-hover:text-white">
                                    <svg-icon
                                        class="block fill-current trans-none"
                                        size="16"
                                        style="transition-property: none;"
                                        :type="item.icon"
                                    />
                                </span>
                                <span class="hidden lg:block first-letter:uppercase text-center xl:text-left break-words hyphens-auto">
                                    {{ menuTranslations(item.name) }}

                                    <em v-if="item.moved" class="text-body-sm font-normal">({{ $t('general.moved') }})</em>
                                </span>
                            </router-link>
                        </div>
                    </li>
                </ul>
            </li>
            <li v-if="index < menuItems.length - 1 && $can(menuItems[index+1].requiredPermissions)" class="px-32 mb-32">
                <div class='h-px w-full bg-primary-20'></div>
            </li>
        </ul>
    </nav>
</template>

<script>
import SvgIcon from '../ui/SvgIcon';
import {mapState} from "pinia";
import {useMainStore} from "../../state/store";

export default {
    components: {SvgIcon},
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        companyActive() {
            return this.currentCompany && this.currentCompany.active;
        },
        menuItems() {
            return this.companyActive ? [
                {
                    title: 'general',
                    requiredPermissions: 'settings.general',
                    items: [
                        {
                            route: 'settings.company',
                            name: 'company',
                            icon: 'apartment',
                        },
                        {
                            route: 'settings.users',
                            name: 'users',
                            icon: 'person',
                        },
                        {
                            route: 'settings.roles',
                            name: 'roles',
                            icon: 'format-list-bulleted',
                        },
                        {
                            route: 'settings.documents',
                            name: 'documents',
                            icon: 'description',
                        },
                        // Disabled until we implement company/user specific labels
                        // {
                        //     route: 'settings.app-labels',
                        //     name: 'labels',
                        //     icon: 'text',
                        // },
                    ],
                },
                {
                    title: 'finance',
                    requiredPermissions: 'settings.finance',
                    items: [
                        {
                            route: 'settings.pricing-plans',
                            name: 'pricing_plans',
                            icon: 'payments',
                        },
                        {
                            route: 'settings.billing-details',
                            name: 'billing_details',
                            icon: 'badge',
                        },
                        {
                            route: 'settings.invoices',
                            name: 'invoices',
                            icon: 'request-quote',
                        },
                    ],
                },
                {
                    title: 'jobsite',
                    requiredPermissions: 'settings.jobsite',
                    items: [
                        {
                            route: 'settings.languages',
                            name: 'languages',
                            icon: 'language',
                        },
                        {
                            route: 'settings.custom-css',
                            name: 'custom_css',
                            icon: 'code',
                        },
                        {
                            route: 'settings.tracking',
                            name: 'tracking',
                            icon: 'readiness-score',
                        },
                        {
                            route: 'settings.jobChannels',
                            name: 'jobchannels',
                            icon: 'rss-feed',
                        },
                        {
                            route: 'settings.privacy',
                            name: 'privacy',
                            icon: 'description',
                        },
                        {
                            route: 'settings.metadata',
                            name: 'metadata',
                            icon: 'info',
                        },
                        {
                            route: 'settings.favicon',
                            name: 'favicon',
                            icon: 'star',
                        },
                        {
                            route: 'settings.labels',
                            name: 'labels',
                            icon: 'title',
                        },
                    ],
                },
                {
                    title: 'vacancies',
                    requiredPermissions: 'settings.vacancies',
                    items: [
                        {
                            route: 'settings.vacancy-reasons',
                            name: 'vacancy_reasons',
                            icon: 'title'
                        },
                        {
                            route: 'settings.competence-templates',
                            name: 'competence-templates',
                            icon: 'license',
                        },
                        {
                            route: 'settings.filter-groups',
                            name: 'filter-groups',
                            icon: 'filter-alt',
                        },
                    ]
                },
                {
                    title: 'applications',
                    requiredPermissions: 'settings.applications',
                    items: [
                        {
                            route: 'settings.locations',
                            name: 'locations',
                            icon: 'location-on',
                        },
                        {
                            route: 'settings.email-templates',
                            name: 'email',
                            icon: 'mail',
                        },
                        {
                            route: 'settings.interview-templates',
                            name: 'interview',
                            icon: 'forum',
                        },
                        {
                            route: 'settings.tags',
                            name: 'tags',
                            icon: 'sell',
                        },
                        {
                            route: 'settings.reject-reasons',
                            name: 'reject_reasons',
                            icon: 'format-quote',
                        },
                    ],
                },
                {
                    title: 'advanced',
                    requiredPermissions: 'settings.advanced',
                    items: [
                        {
                            route: 'settings.gdpr',
                            name: 'gdpr',
                            icon: 'shield-locked',
                        },
                        {
                            route: 'settings.api-keys',
                            name: 'key',
                            icon: 'key',
                        },
                        {
                            route: 'settings.integrations',
                            name: 'integrations',
                            icon: 'extension',
                        },
                    ],
                }
            ] : [
                {
                    title: 'finance',
                    requiredPermissions: 'settings.finance',
                    items: [
                        {
                            route: 'settings.pricing-plans',
                            name: 'pricing_plans',
                            icon: 'payments',
                        },
                        {
                            route: 'settings.billing-details',
                            name: 'billing_details',
                            icon: 'badge',
                        },
                        {
                            route: 'settings.invoices',
                            name: 'invoices',
                            icon: 'request-quote',
                        },
                    ],
                },
            ];
        },
    },
    methods: {
        menuTranslations(label) {
            return this.$t(`settings.menu.${label}`);
        },
    },
    mounted() {
        // Check what route to go to!
        const currentRouteMenuItem = this.menuItems.find(i => i.items.find(ii => ii.route === this.$route.name));
        const hasAccessToCurrentRoute = this.$can(currentRouteMenuItem.requiredPermissions);

        if (hasAccessToCurrentRoute) {
            return;
        }

        const firstMenuItem = this.menuItems.find(i => this.$can(i.requiredPermissions));

        if (firstMenuItem) {
            this.$router.push({
                name: firstMenuItem.items[0].route,
            });
        }
    },
};
</script>

<style scoped>
.router-link-active {
    & span:first-of-type {
        @apply bg-secondary text-white;
    }

    & div:first-of-type {
        @apply block;
    }
}
</style>
